/**
 * KPIStatusBadge — Green/yellow/red status indicator for KPI progress.
 * Also shows status labels for non-color states (not_started, completed, etc.)
 */
import React from 'react';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import TrendingDown from 'lucide-react/dist/esm/icons/trending-down';
import Minus from 'lucide-react/dist/esm/icons/minus';
import Check from 'lucide-react/dist/esm/icons/check';
import Clock from 'lucide-react/dist/esm/icons/clock';
import { useUIStore } from '../../../stores/uiStore';
import { getTranslation } from '../../../i18n';

interface KPIStatusBadgeProps {
  percentage: number | null;
  status: string;
  thresholds?: { green_min?: number; yellow_min?: number } | null;
  size?: 'sm' | 'md';
}

const KPIStatusBadge: React.FC<KPIStatusBadgeProps> = ({
  percentage,
  status,
  thresholds,
  size = 'sm',
}) => {
  const { language } = useUIStore();
  const t = (key: string) => getTranslation(language, key);

  const greenMin = thresholds?.green_min ?? 80;
  const yellowMin = thresholds?.yellow_min ?? 50;

  let color: string;
  let label: string;
  let Icon: React.FC<any> = Minus;

  if (status === 'completed') {
    color = 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20';
    label = t('perfKpiStatusCompleted');
    Icon = Check;
  } else if (status === 'missed' || status === 'cancelled') {
    color = 'text-content-tertiary bg-surface-secondary border-default';
    label = status === 'missed' ? t('perfKpiStatusMissed') : t('perfKpiStatusCancelled');
  } else if (status === 'pending_approval') {
    color = 'text-blue-400 bg-blue-400/10 border-blue-400/20';
    label = t('perfKpiStatusPending');
    Icon = Clock;
  } else if (percentage === null || percentage === undefined) {
    color = 'text-content-tertiary bg-surface-secondary border-default';
    label = t('perfKpiStatusNotStarted');
    Icon = Clock;
  } else if (percentage >= greenMin) {
    color = 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20';
    label = t('perfKpiStatusOnTrack');
    Icon = TrendingUp;
  } else if (percentage >= yellowMin) {
    color = 'text-amber-400 bg-amber-400/10 border-amber-400/20';
    label = t('perfKpiStatusAtRisk');
    Icon = Minus;
  } else {
    color = 'text-status-error bg-status-error/10 border-status-error/20';
    label = t('perfKpiStatusBehind');
    Icon = TrendingDown;
  }

  const sizeClasses = size === 'sm'
    ? 'text-[11px] px-2 py-0.5 gap-1'
    : 'text-xs px-2.5 py-1 gap-1.5';

  return (
    <span className={`inline-flex items-center ${sizeClasses} rounded-md font-medium border ${color}`}>
      <Icon size={size === 'sm' ? 10 : 12} />
      {label}
    </span>
  );
};

export default React.memo(KPIStatusBadge);
